import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { useCaseCounts } from '../CaseCounts';
import './onHoldAlert.css';

/**
 * A red alert the moment one of the doctor's cases is put on hold.
 *
 * On hold is the one queue that waits on the doctor — nothing moves until
 * they fix the data — so a quiet bubble is not enough. The server only
 * sends `onhold_cases` to a doctor, so this never shows for an admin or a
 * technician.
 *
 * Each hold is announced once: acknowledging it records the case and when
 * it was last updated, per user, so the alert comes back if the case is
 * put on hold again later, but not on every page load in between.
 */
const storageKey = (userId) => `onhold-alert-seen-${userId || 'anon'}`;

const readSeen = (userId) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(userId))) || {};
  } catch (e) {
    return {};
  }
};

const writeSeen = (userId, seen) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(seen));
  } catch (e) { /* storage blocked - the alert just shows again */ }
};

const holdStamp = (each) => String(each.updated_at || '');

const OnHoldAlert = () => {
  const navigate = useNavigate();
  const { onhold_cases: held } = useCaseCounts();
  const userId = useSelector((state) => state.auth.authUser?.id);
  const [seen, setSeen] = useState(() => readSeen(userId));

  useEffect(() => { setSeen(readSeen(userId)); }, [userId]);

  const unseen = useMemo(
    () => (held || []).filter((each) => seen[each.id] !== holdStamp(each)),
    [held, seen]
  );

  const acknowledge = () => {
    const next = { ...seen };
    unseen.forEach((each) => { next[each.id] = holdStamp(each); });
    writeSeen(userId, next);
    setSeen(next);
  };

  const openCases = () => {
    const target = unseen.length === 1 ? `/cases/details/${unseen[0].id}` : '/cases/on-hold';
    acknowledge();
    navigate(target);
  };

  return (
    <Modal
      open={ unseen.length > 0 }
      onCancel={ acknowledge }
      footer={ null }
      centered
      width={ 480 }
      maskClosable={ false }
      wrapClassName="onhold-alert-wrap"
    >
      <div className="onhold-alert" role="alert">
        <span className="onhold-alert-mark">
          <Icon icon="tabler:alert-triangle-filled" width={ 30 } />
        </span>
        <h2 className="onhold-alert-title">
          { unseen.length === 1 ? 'A case was put on hold' : `${unseen.length} cases were put on hold` }
        </h2>
        <p className="onhold-alert-lead">
          The technician could not work with the submitted data. Nothing moves until you fix it.
        </p>

        <ul className="onhold-alert-list">
          { unseen.map((each) => (
            <li key={ each.id }>
              <span className="onhold-alert-case">
                <strong>{ each.name || 'Unknown patient' }</strong>
                <span className="mono">{ each.case_number }</span>
              </span>
              { each.reject_reason &&
                <span className="onhold-alert-reason">Reason: { each.reject_reason }</span> }
            </li>
          )) }
        </ul>

        <div className="onhold-alert-actions">
          <button type="button" className="onhold-alert-btn is-quiet" onClick={ acknowledge }>
            Later
          </button>
          <button type="button" className="onhold-alert-btn" onClick={ openCases }>
            { unseen.length === 1 ? 'Open case' : 'View on-hold cases' }
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OnHoldAlert;
